import React, { useEffect, useState } from "react";

// Hook to detect mobile devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

/* ----------------------- Styles ----------------------- */



const sideCardStyle = {
  container: {
    width: "250px",
    display: "none",
    flexDirection: "column",
    gap: "12px",
  },
  card: {
    backgroundColor: "#01304e",
    borderRadius: "8px",
    overflow: "hidden",
    marginBottom: "12px",
  },
  imageContainer: {
    width: "100%",
    height: "120px",
    backgroundColor: "#01304e",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "none",
    opacity: 0.6,
  },
  content: {
    padding: "16px",
    textAlign: "center",
  },
  title: {
    color: "white",
    fontWeight: "bold",
    marginBottom: "4px",
    fontSize: "13px",
  },
  amount: {
    color: "#FFD700",
    marginBottom: "12px",
  },
  button: {
    background: "linear-gradient(to right, #8997FF, #3D04A6, #001EFF)",
    color: "white",
    padding: "8px 16px",
    borderRadius: "6px",
    width: "100%",
    border: "none",
    cursor: "pointer",
  },
};

const mainCardStyle = {
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  img: {
    width: "100px",
  },
  card: {
    backgroundColor: "#01304e",
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "46px",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  logo: {
    backgroundColor: "#16a34a",
    color: "white",
    padding: "8px 16px",
    borderRadius: "6px",
    fontWeight: "bold",
  },
  textSection: {
    color: "white",
  },
  title: {
    fontWeight: "bold",
    fontSize: "12px",
  },
  amount: {
    color: "#FFD700",
  },
  button: {
    background: "linear-gradient(to right, #8997FF, #3D04A6, #001EFF)",
    color: "white",
    padding: "8px 16px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
  },
  buttons: {
    background: "linear-gradient(to right, #8997FF, #3D04A6, #001EFF)",
    color: "white",
    padding: "8px 16px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
  },
};

// Mobile styles
const mobileStyles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#01305e",
    padding: "0",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "8px",
    padding: "9px",
    width: "100%",
    boxSizing: "border-box",
  },
  card: {
    backgroundColor: "#01304e",
    borderRadius: "8px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "180px",
    width: "100%",
  },
  cardImage: {
    width: "100%",
    height: "90%",
  },
  cardContent: {
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "center",
  },
  bonusText: {
    color: "#FFD700",
    fontSize: "0.7rem",
    fontWeight: "bold",
  },
  title: {
    color: "white",
    fontSize: "12px",
    fontWeight: "bold",
    flex: "1",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
  },
  button: {
    background: "linear-gradient(to right, #8997FF, #3D04A6, #001EFF)",
    color: "white",
    padding: "6px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    fontSize: "9px",
    bottom: "0",
  },
  buttons: {
    background: "linear-gradient(to right, #8997FF, #3D04A6, #001EFF)",
    color: "white",
    padding: "8px 16px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    width: "100%",
    fontSize: "14px",
  },
  tabs: {
    display: "flex",
    borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
    padding: "0 16px",
  },
  tab: {
    padding: "12px 16px",
    color: "white",
    backgroundColor: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    marginBottom: "-2px",
    cursor: "pointer",
    fontSize: "14px",
  },
  tabActive: {
    borderBottom: "2px solid #FFD700",
    color: "#FFD700",
  },
  listCard: {
    backgroundColor: "#01304e",
    borderRadius: "8px",
    padding: "12px",
    margin: "8px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  logo: {
    width: "90px",
    height: "80px",
    borderRadius: "4px",
  },
  listContent: {
    flex: 1,
  },
};

/* ----------------------- Components ----------------------- */

// Desktop Carousel
const DesktopCarousel = ({ children }) => {
  const items = React.Children.toArray(children)
    .filter(Boolean)
    .filter(child => child.props.data.AddPlacementZone === "Top")
    .sort((a, b) => a.props.data.DisplayOrder - b.props.data.DisplayOrder);

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 8; // 4 columns x 2 rows
  const totalPages = Math.ceil(items.length / itemsPerPage);

  const goNext = () => currentPage < totalPages - 1 && setCurrentPage((prev) => prev + 1);
  const goPrev = () => currentPage > 0 && setCurrentPage((prev) => prev - 1);

  return (
    <div style={{ position: "relative", padding: "0 40px" }}>
      {totalPages > 1 && (
        <>
          <button
            onClick={goPrev}
            style={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(0,0,0,0.5)",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              cursor: "pointer",
            }}
          >
            ←
          </button>
          <button
            onClick={goNext}
            style={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(0,0,0,0.5)",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              cursor: "pointer",
            }}
          >
            →
          </button>
        </>
      )}

      <div style={{ overflow: "hidden", position: "relative", width: "100%" }}>
        <div
          style={{
            display: "flex",
            transition: "transform 0.2s ease-out",
            transform: `translateX(-${(100 / totalPages) * currentPage}%)`,
            width: `${totalPages * 100}%`,
          }}
        >
          {[...Array(totalPages)].map((_, pageIndex) => (
            <div
              key={pageIndex}
              style={{
                flex: `0 0 ${100 / totalPages}%`,
                padding: "0 8px",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "16px",
                  padding: "16px 0",
                }}
              >
                {items.slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginTop: "16px",
          }}
        >
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index)}
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                backgroundColor: currentPage === index ? "#FFD700" : "rgba(255,255,255,0.3)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};


// Desktop Card
const DesktopCard = ({ data }) => (
  <div style={sideCardStyle.card}>
    <div style={sideCardStyle.imageContainer}>
      <img
        src={data?.ImageBlobUrl}
        alt="Preview"
        style={{ width: "100%", height: "100%", objectFit: "none", opacity: 0.8 }}
      />
    </div>
    <div style={sideCardStyle.content}>
      <h1 style={{ ...sideCardStyle.title, fontSize: "14px" }}>
        {data?.LabelText
          ? `${data.LabelText.toUpperCase()}`
          : "JOIN NOW"}
      </h1>
      <div style={sideCardStyle.amount}>{data?.BonusAmountText }</div>
      <a href={data?.RedirectUrl} target="_blank" >
        <button style={sideCardStyle.button}>GO TO SITE</button>
      </a>
    </div><label htmlFor=""></label>
  </div>
);

const smallStyles = {
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "8px",
    padding: "9px",
    width: "100%",
    boxSizing: "border-box",
  },
  card: {
    backgroundColor: "#01304e",
    borderRadius: "8px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "180px",
    width: "100%",
  },
  cardImage: {
    width: "100%",
    height: "120px",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#012339",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    maxHeight: "120px",
    display: "block",
  },
  cardContent: {
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
    justifyContent: "space-between",
  },
  titleWrapper: {
    minHeight: "28px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    color: "white",
    fontSize: "12px",
    fontWeight: 550,
    overflow: "hidden",
    margin: 0,
    textAlign: "center",
    width: "100%",
    maxWidth: "100%",
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
  },
  bonusWrapper: {
    minHeight: "20px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  bonusText: {
    color: "#FFD700",
    fontSize: "0.7rem",
    fontWeight: "bold",
    textAlign: "center",
    margin: 0,
    padding: 0,
    lineHeight: "1",
  },
  buttonWrapper: {
    width: "100%",
    textDecoration: "none",
  },
  button: {
    background: "linear-gradient(to right, #8997FF, #3D04A6, #001EFF)",
    color: "white",
    padding: "8px 12px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    fontSize: "10px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    width: "100%",
    display: "block",
  }
};


// Mobile Card
const MobileCard = ({ data }) => {
  return (
    <div style={smallStyles.card}>
    <div style={smallStyles.cardImage}>
      <img
        src={data?.ImageBlobUrl || "/api/placeholder/400/320"}
        alt="Betting site"
        style={smallStyles.image}
      />
    </div>
    
    <div style={smallStyles.cardContent}>
      <div style={smallStyles.titleWrapper}>
        <h1 style={{ ...smallStyles.title, fontSize: "12px" }}>
          {data?.LabelText
            ? data?.LabelText.toUpperCase()
            : "JOIN NOW"}
        </h1>

      </div>
      <div style={smallStyles.bonusWrapper}>
        <p style={smallStyles.bonusText}>{data?.BonusAmountText}</p>
      </div>
      <a
        href={data?.RedirectUrl}
        target="_blank"
        
        style={smallStyles.buttonWrapper}
      >
        <button style={smallStyles.button}>GO TO SITE</button>
      </a>
    </div>
  </div>
  );
};

// Mobile Carousel
const MobileCarousel = ({ children }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const items = React.Children.toArray(children)
    .filter(Boolean)
    .filter(child => child.props.data.AddPlacementZone === "Top")
    .sort((a, b) => a.props.data.DisplayOrder - b.props.data.DisplayOrder);
  
  console.log(items);
  const itemsPerPage = 6;
  const totalPages = Math.ceil(items.length / itemsPerPage);

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.touches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > 50) {
      if (distance > 0 && currentPage < totalPages - 1) {
        setCurrentPage((prev) => prev + 1);
      } else if (distance < 0 && currentPage > 0) {
        setCurrentPage((prev) => prev - 1);
      }
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <div style={{ overflow: "hidden", position: "relative", width: "100%", paddingBottom: "24px" }}>
      <div
        style={{
          display: "flex",
          transition: "transform 0.3s ease-out",
          transform: `translateX(-${(600 / totalPages) * currentPage}%)`,
          width: "100%",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {[...Array(totalPages)].map((_, pageIndex) => {
          const pageItems = items.slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage);
          return (
            <div key={pageIndex} style={{ flex: "0 0 100%", boxSizing: "border-box" }}>
              <div style={mobileStyles.cardGrid}>{pageItems}</div>
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div
          style={{
            position: "absolute",
            bottom: "0",
            left: "0",
            right: "0",
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            padding: "8px 0",
          }}
        >
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index)}
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: currentPage === index ? "#FFD700" : "rgba(255, 255, 255, 0.5)",
                border: "none",
                padding: "0",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};


// Mobile List Card (uses passed cards)
const MobileListCard = ({ cards }) => {
  return (
    <div>
      {Array.isArray(cards) &&
        cards
          .filter((el) => el.AddPlacementZone === "Center")
          .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
          .map((card, index) => (
            <div key={index} style={mobileStyles.listCard}>
              <div style={mobileStyles.logo}>
                <img
                  src={card.ImageBlobUrl || "/api/placeholder/400/320"}
                  alt="Betting site"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </div>
              <div style={mobileStyles.listContent}>
                <h1 style={{ ...mobileStyles.title, fontSize: "12px" }}>
                  {card.LabelText
                    ? `${card.LabelText.toUpperCase()}`
                    : "JOIN NOW"}
                </h1>
                <span style={mobileStyles.bonusText}>{card.BonusAmountText}</span>
              </div>
              <a href={card.RedirectUrl} target="_blank" >  
                <button style={mobileStyles.buttons}>GO TO SITE</button>
              </a>
            </div>
          ))}
    </div>
  );
};

// Mobile View
const MobileView = ({ cards }) => {
  return (
    <div>
      <MobileCarousel>
        {cards.map((card, index) => (
          <MobileCard key={card.id || index} data={card} />
        ))}
      </MobileCarousel>
      <div style={{ marginTop: "16px" }}>
     
        <div>
          <MobileListCard cards={cards} />
        </div>
      </div>
    </div>
  );
};

// Tab Navigation (Desktop)


// Side Card for Left Panel
const SideCard = ({ cards }) => {
  return (
    <div>
      {Array.isArray(cards) &&
        cards
          .filter((el) => el.AddPlacementZone === "Left")
          .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
          .map((post, index) => (
            <div key={index} style={sideCardStyle.card}>
              <div style={sideCardStyle.imageContainer}>
                <img
                  src={post.ImageBlobUrl}
                  alt="Preview"
                  style={sideCardStyle.image}
                />
              </div>
              <div style={sideCardStyle.content}>
                <div style={sideCardStyle.title}>
                  {post.LabelText
                    ? `${post.LabelText.toUpperCase()}`
                    : "JOIN NOW"}
                </div>
                <div style={sideCardStyle.amount}>{post.BonusAmountText}</div>
                <a href={post.RedirectUrl} target="_blank" >
                  <button style={sideCardStyle.button}>GO TO SITE</button>
                </a>
              </div>
            </div>
          ))}
    </div>
  );
};

const SidePanel = ({ cards }) => {
  return <div style={sideCardStyle.container}><SideCard cards={cards} /></div>;
};

// Side Card for Right Panel
const SideCardRight = ({ cards }) => {
  return (
    <div>
      {Array.isArray(cards) &&
        cards
          .filter((el) => el.AddPlacementZone === "Right")
          .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
          .map((post, index) => (
            <div key={index} style={sideCardStyle.card}>
              <div style={sideCardStyle.imageContainer}>
                <img
                  src={post.ImageBlobUrl}
                  alt="Betting preview"
                  style={sideCardStyle.image}
                />
              </div>
              <div style={sideCardStyle.content}>
                <div style={sideCardStyle.title}>
                  {post.LabelText
                    ? `${post.LabelText.toUpperCase()}`
                    : "JOIN NOW"}
                </div>
                <div style={sideCardStyle.amount}>{post.BonusAmountText }</div>
                <a href={post.RedirectUrl} target="_blank" >
                  <button style={sideCardStyle.button}>GO TO SITE</button>
                </a>
              </div>
            </div>
          ))}
    </div>
  );
};

const SidePanelRight = ({ cards }) => {
  return <div style={sideCardStyle.container}><SideCardRight cards={cards} /></div>;
};

// Main Card (for Desktop Middle Panel)
const MainCard = ({ data }) => (
  <div style={mainCardStyle.card}>
    <div style={mainCardStyle.leftSection}>
      <img style={mainCardStyle.img} src={data.ImageBlobUrl} alt="Card Icon" />
      <div style={mainCardStyle.textSection}>
        <div style={mainCardStyle.title}>
          {data?.LabelText
            ? `${data.LabelText.toUpperCase()}`
            : "JOIN NOW"}
        </div>
        <div style={mainCardStyle.amount}>{data?.BonusAmountText }</div>
      </div>
    </div>
    <a href={data.RedirectUrl} target="_blank" >
      <button style={mainCardStyle.buttons}>GO TO SITE</button>
    </a>
  </div>
);



const MainPanel = ({ cards }) => {
  const middleCards = cards.filter((card) => card.AddPlacementZone === 'Center').sort((a, b) => a.DisplayOrder - b.DisplayOrder);
  return (
    <div style={mainCardStyle.container}>
      {middleCards.map((post, index) => (
        <MainCard key={index} data={post} />
      ))}
    </div>
  );
};

/* ----------------------- Main Layout ----------------------- */

const BettingLayout = ({cards}) => {
  const isMobile = useIsMobile();

 

  if (isMobile) {
    return (
      <div style={mobileStyles.container}>
        <MobileView cards={cards} />
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #00152D, #002C5D, #004693)",
      }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", display: "flex", flexDirection: "column" }}>
        <DesktopCarousel>
          {cards.map((card, index) => (
            <DesktopCard key={index} data={card} />
          ))}
        </DesktopCarousel>
        <div style={{ display: "flex", gap: "16px" }}>
          <SidePanel cards={cards} />
          <div style={{ flex: 1 }}>

            <MainPanel cards={cards} />
          </div>
          <SidePanelRight cards={cards} />
        </div>
      </div>
    </div>
  );
};

export default BettingLayout;
